import type { Patient, Reference } from '@medplum/fhirtypes'
import type { SeenMedplumClient } from '@seen/core/client'
import { ConditionOrdering } from '@seen/core/models/conditions/ConditionOrdering'
import { Diagnosis } from '@seen/core/models/conditions/Diagnosis'
import { getReferenceString } from '@seen/core/references'
import { useMemo } from 'react'
import { useConditionOrdering } from '#src/hooks/useConditionOrdering.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

export interface UseOrderedConditionsResult {
  orderedConditions: Diagnosis[]
  updateOrdering: (orderedReferenceStrings: string[]) => void
  isUpdating: boolean
}

export interface UseOrderedConditionsListResult extends UseOrderedConditionsResult {
  conditions: Diagnosis[] | undefined
  isLoading: boolean
}

export interface UseOrderedConditionsOptions {
  // Off, conditions keep the body-system order of `Diagnosis.sortForDisplay`
  // and the participant's ConditionOrdering list is never fetched or written.
  manualOrder?: boolean
}

export function orderConditionsByPreference(
  baseSortedConditions: Diagnosis[],
  preferredReferenceStrings: string[]
): Diagnosis[] {
  if (preferredReferenceStrings.length === 0) {
    return baseSortedConditions
  }

  const baseSortedRefStrings = baseSortedConditions.map((c) =>
    getReferenceString(c.reference)
  )
  const conditionMap = new Map(
    baseSortedConditions.map((c) => [getReferenceString(c.reference), c])
  )
  const conditionIdSet = new Set(baseSortedRefStrings)
  const orderedFromStore = preferredReferenceStrings.filter((id) =>
    conditionIdSet.has(id)
  )
  const orderedSet = new Set(orderedFromStore)
  const newConditions = baseSortedRefStrings.filter((id) => !orderedSet.has(id))
  const finalOrderedRefStrings = [...newConditions, ...orderedFromStore]

  return finalOrderedRefStrings.flatMap((refString) => {
    const condition = conditionMap.get(refString)
    return condition !== undefined ? [condition] : []
  })
}

export async function fetchOrderedCurrentConditions(
  medplum: SeenMedplumClient,
  participantReference: Reference<Patient>,
  { manualOrder = true }: UseOrderedConditionsOptions = {}
): Promise<Diagnosis[]> {
  const [conditions, orderingResults] = await Promise.all([
    medplum.searchResourceClass(
      Diagnosis,
      Diagnosis.searchQuery({ subject: participantReference })
    ),
    manualOrder
      ? medplum.searchResourceClass(
          ConditionOrdering,
          ConditionOrdering.byPatient(participantReference)
        )
      : [],
  ])

  const baseSorted = Diagnosis.sortForDisplay(
    conditions.filter((c) => c.isCurrent)
  )
  return orderConditionsByPreference(
    baseSorted,
    orderingResults[0]?.conditions.map(getReferenceString) ?? []
  )
}

export function useOrderedConditions(
  patientReferenceString: string,
  conditions: Diagnosis[] | undefined,
  { manualOrder = true }: UseOrderedConditionsOptions = {}
): UseOrderedConditionsResult {
  const baseSortedConditions = useMemo(
    () =>
      Diagnosis.sortForDisplay(conditions?.filter((c) => c.isCurrent) ?? []),
    [conditions]
  )

  const conditionReferenceStrings = useMemo(
    () => baseSortedConditions.map((c) => getReferenceString(c.reference)),
    [baseSortedConditions]
  )

  const { orderedReferenceStrings, updateOrdering, isUpdating } =
    useConditionOrdering(
      manualOrder ? patientReferenceString : '',
      conditionReferenceStrings
    )

  const orderedConditions = useMemo(() => {
    if (!manualOrder) {
      return baseSortedConditions
    }
    return orderConditionsByPreference(
      baseSortedConditions,
      orderedReferenceStrings
    )
  }, [manualOrder, baseSortedConditions, orderedReferenceStrings])

  return {
    orderedConditions,
    updateOrdering,
    isUpdating,
  }
}

export function useOrderedConditionsList(
  patientReference: Reference<Patient> | undefined,
  options?: UseOrderedConditionsOptions
): UseOrderedConditionsListResult {
  const { data: conditions, isLoading } = useSearch(
    Diagnosis,
    patientReference
      ? Diagnosis.searchQuery({
          subject: patientReference,
        })
      : undefined
  )

  const { orderedConditions, updateOrdering, isUpdating } =
    useOrderedConditions(patientReference?.reference ?? '', conditions, options)

  return {
    conditions,
    isLoading,
    orderedConditions,
    updateOrdering,
    isUpdating,
  }
}
